import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight } from '@phosphor-icons/react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import Page from '@/components/page/Page';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import paths from '@/paths';
import {
  MOCK_AR_OVERVIEW,
  type ARCollectionsMetrics,
  type ARTrendDataPoint,
} from '../data/mockARData';

type DateRange = 'today' | 'mtd' | 'ytd';

// Format currency
function formatCurrency(amount: number, compact = false): string {
  if (compact) {
    if (amount >= 1_000_000) {
      return `$${(amount / 1_000_000).toFixed(2)}M`;
    }
    if (amount >= 1_000) {
      return `$${(amount / 1_000).toFixed(1)}K`;
    }
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// Format number with commas
function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

// Simple Stat Card (matches AgingBucketCard pattern)
function StatCard({
  label,
  value,
  subValue,
  valueColor,
}: {
  label: string;
  value: string;
  subValue?: string;
  valueColor?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="text-sm text-muted-foreground mb-1">{label}</div>
        <div className="flex items-baseline gap-2">
          <span
            className="text-2xl font-bold"
            style={valueColor ? { color: valueColor } : undefined}
          >
            {value}
          </span>
          {subValue && (
            <span className="text-sm text-muted-foreground">{subValue}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// AI Impact Section (toned down)
function AIImpactSection({ dateRange }: { dateRange: DateRange }) {
  const data = MOCK_AR_OVERVIEW;

  // Adjust values based on date range for demo
  const multiplier =
    dateRange === 'today' ? 0.01 : dateRange === 'mtd' ? 0.15 : 1;
  const collected = data.collectedWithAI * multiplier;
  const automated = Math.round(data.callsEmailsAutomated * multiplier);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          AI Impact
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(collected, true)}
            </p>
            <p className="text-sm text-muted-foreground">collected w/ AI</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{data.aiCollectionRate}%</p>
            <p className="text-sm text-muted-foreground">AI collection rate</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{formatNumber(automated)}</p>
            <p className="text-sm text-muted-foreground">
              calls/emails automated
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Collections Stats
function CollectionsStats({ metrics }: { metrics: ARCollectionsMetrics }) {
  const navigate = useNavigate();

  return (
    <Card
      className="h-full cursor-pointer transition-colors hover:bg-muted/50"
      onClick={() =>
        navigate({ to: paths.authenticated.accountsReceivable.collections })
      }
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium">Collections</CardTitle>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-2xl font-bold">
              {formatNumber(metrics.outreachSentReceived)}
            </p>
            <p className="text-xs text-muted-foreground">
              Outreach Sent/Received
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(metrics.collectedWithAI)}
            </p>
            <p className="text-xs text-muted-foreground">Collected w/ AI</p>
          </div>
          <div>
            <p className="text-2xl font-bold">
              {formatNumber(metrics.outreachAutomated)}
            </p>
            <p className="text-xs text-muted-foreground">Outreach Automated</p>
          </div>
          <div>
            <p className="text-2xl font-bold">
              {formatCurrency(metrics.totalCollected)}
            </p>
            <p className="text-xs text-muted-foreground">Total Collected</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Snapshot Stats
function SnapshotStats() {
  const { snapshot } = MOCK_AR_OVERVIEW;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Snapshot</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-2xl font-bold">
              {formatNumber(snapshot.openInvoices)}
            </p>
            <p className="text-xs text-muted-foreground">Open Invoices</p>
          </div>
          <div>
            <p className="text-2xl font-bold">
              {formatCurrency(snapshot.outstandingBalance)}
            </p>
            <p className="text-xs text-muted-foreground">Outstanding Balance</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-orange-600">
              {formatNumber(snapshot.overdueInvoices)}
            </p>
            <p className="text-xs text-muted-foreground">Overdue Invoices</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-orange-600">
              {formatCurrency(snapshot.overdueBalance)}
            </p>
            <p className="text-xs text-muted-foreground">Overdue Balance</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Aging Balance Bar Chart
const BUCKET_COLORS = ['#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444'];

function AgingBalanceChart() {
  const { agingBuckets } = MOCK_AR_OVERVIEW;

  const chartData = agingBuckets.map((bucket, index) => ({
    name: bucket.label,
    value: bucket.amount,
    color: BUCKET_COLORS[index],
  }));

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Aging Report</CardTitle>
        <p className="text-xs text-muted-foreground">
          Open invoice balance by aging bucket
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis
                tickFormatter={(v) => formatCurrency(v, true)}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(value: number) => [
                  formatCurrency(value),
                  'Balance',
                ]}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Line Chart Component
function TrendLineChart({
  title,
  description,
  data,
  color,
  formatYAxis,
  formatTooltip,
}: {
  title: string;
  description: string;
  data: ARTrendDataPoint[];
  color: string;
  formatYAxis?: (v: number) => string;
  formatTooltip?: (v: number) => string;
}) {
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">{title}</CardTitle>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardHeader>
      <CardContent>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis
                tickFormatter={formatYAxis || ((v) => String(v))}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(value: number) => [
                  formatTooltip ? formatTooltip(value) : value,
                  title,
                ]}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                dot={{ r: 3, fill: color }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Task Summary Pie Chart
const TASK_COLORS = {
  completed: '#22c55e',
  inProgress: '#3b82f6',
  pending: '#eab308',
  failed: '#ef4444',
};

function TaskSummaryChart() {
  const { taskSummary } = MOCK_AR_OVERVIEW;

  const pieData = [
    {
      name: 'Completed',
      value: taskSummary.completed,
      color: TASK_COLORS.completed,
    },
    {
      name: 'In Progress',
      value: taskSummary.inProgress,
      color: TASK_COLORS.inProgress,
    },
    { name: 'Pending', value: taskSummary.pending, color: TASK_COLORS.pending },
    { name: 'Failed', value: taskSummary.failed, color: TASK_COLORS.failed },
  ];

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Task Summary</CardTitle>
        <p className="text-xs text-muted-foreground">Tasks by status</p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <div className="h-[160px] w-[160px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [formatNumber(value), 'Tasks']}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-2">
            {pieData.map((item) => (
              <div key={item.name} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm flex-1">{item.name}</span>
                <span className="text-sm font-medium">
                  {formatNumber(item.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Page Component
export function AROverviewPage() {
  const [dateRange, setDateRange] = useState<DateRange>('today');
  const data = MOCK_AR_OVERVIEW;

  // Get metrics based on date range
  const metricsMap: Record<DateRange, ARCollectionsMetrics> = {
    today: data.collectionsToday,
    mtd: data.collectionsYTD, // Using YTD as MTD for demo
    ytd: data.collectionsAll,
  };
  const currentMetrics = metricsMap[dateRange];

  // Adjust summary values based on date range for demo
  const getAdjustedValue = (baseValue: number) => {
    const multiplier =
      dateRange === 'today' ? 0.03 : dateRange === 'mtd' ? 0.25 : 1;
    return baseValue * multiplier;
  };

  return (
    <Page title="AR Overview">
      <div className="space-y-6">
        {/* Date Range Filter */}
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-muted-foreground">
            Your accounts at a glance
          </h2>
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            {(['today', 'mtd', 'ytd'] as const).map((range) => (
              <Button
                key={range}
                variant={dateRange === range ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-3 text-xs"
                onClick={() => setDateRange(range)}
              >
                {range === 'today'
                  ? 'Today'
                  : range === 'mtd'
                    ? 'Month'
                    : 'YTD'}
              </Button>
            ))}
          </div>
        </div>

        {/* Summary Stats Row */}
        <div className="grid grid-cols-5 gap-4">
          <StatCard
            label="Total Outstanding"
            value={formatCurrency(data.totalOutstanding, true)}
          />
          <StatCard
            label="Total Overdue"
            value={formatCurrency(data.totalOverdue, true)}
            valueColor="#ea580c"
          />
          <StatCard
            label="Cash Collected"
            value={formatCurrency(
              getAdjustedValue(data.cashCollectedThisMonth),
              true,
            )}
            valueColor="#16a34a"
          />
          <StatCard
            label="Invoices Followed Up"
            value={formatNumber(
              Math.round(getAdjustedValue(data.invoicesFollowedUpThisMonth)),
            )}
          />
          <StatCard
            label="Invoices Collected"
            value={formatNumber(
              Math.round(getAdjustedValue(data.invoicesCollectedThisMonth)),
            )}
          />
        </div>

        {/* AI Impact Section */}
        <AIImpactSection dateRange={dateRange} />

        {/* Charts Grid - 3 columns, 2 rows */}
        <div className="grid grid-cols-3 gap-4">
          {/* Row 1 */}
          <CollectionsStats metrics={currentMetrics} />
          <SnapshotStats />
          <AgingBalanceChart />

          {/* Row 2 */}
          <TrendLineChart
            title="Overdue Balance %"
            description="Percentage of open balance that is overdue"
            data={data.overdueBalanceTrend}
            color="#f97316"
            formatYAxis={(v) => `${v}%`}
            formatTooltip={(v) => `${v}%`}
          />
          <TrendLineChart
            title="Cash Collected"
            description="Monthly cash collected over time"
            data={data.cashCollectedTrend}
            color="#22c55e"
            formatYAxis={(v) => `$${v}M`}
            formatTooltip={(v) => `$${v}M`}
          />
          <TaskSummaryChart />
        </div>
      </div>
    </Page>
  );
}
